import { ActionIcon, Box, Center, Flex, Text } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { clampIndex } from '../utils/clampIndex';

type AdviceFile = { id: string; name: string };

const stripExtension = (name: string) => name.replace(/\.[^/.]+$/, '');

type Props = {
  files: AdviceFile[];
  initialIndex?: number;
  onIndexChange?: (index: number) => void;
};

export function AdviceHeadlineCarousel({ files, initialIndex = 0, onIndexChange }: Props) {
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    if (files.length === 0) return;
    const next = clampIndex(initialIndex, files.length);
    setIndex(prev => (prev === next ? prev : next));
  }, [initialIndex, files.length]);

  useEffect(() => {
    if (files.length === 0) return;
    setIndex(prev => clampIndex(prev, files.length));
  }, [files.length]);

  useEffect(() => {
    onIndexChange?.(index);
  }, [index, onIndexChange]);

  const title = useMemo(() => {
    const current = files[index];
    return current ? stripExtension(current.name) : '—';
  }, [files, index]);

  const canPrev = index > 0;
  const canNext = index < files.length - 1;

  const goPrev = () => setIndex(i => Math.max(0, i - 1));
  const goNext = () => setIndex(i => Math.min(files.length - 1, i + 1));

  const dots = useMemo(() => Array.from({ length: files.length }, (_, i) => i), [files.length]);

  return (
    <Flex w="100%" align={'center'}>
      <Box>
        {files.length > 1 && (
          <ActionIcon
            onClick={goPrev}
            disabled={!canPrev}
            radius={16}
            variant="filled"
            opacity={canPrev ? 1 : 0.2}
            bg="rgba(0,0,0,0.20)"
            w={32}
            h={40}
            style={{
              borderRadius: 16,
              backdropFilter: 'blur(10px)',
            }}
          >
            <IconChevronLeft size={18} />
          </ActionIcon>
        )}
      </Box>

      <Box flex={1} py="xs">
        {files.length > 1 ? (
          <Center>
            <Flex align="center" gap="xxxs" pb="xxs">
              {dots.map(i => (
                <Box
                  key={i}
                  w={i === index ? 22 : 8}
                  h={8}
                  opacity={i === index ? 1 : 0.35}
                  bg="rgba(255,255,255,0.9)"
                  style={{
                    borderRadius: 999,
                    transition: 'all 160ms ease',
                  }}
                />
              ))}
            </Flex>
          </Center>
        ) : null}

        <Box
          style={{
            gridTemplateColumns: '32px 1fr 32px',
            alignItems: 'center',
          }}
        >
          <Text
            c="white"
            fw={FONT_WEIGHTS.bold}
            size="xl"
            ta="center"
            px="md"
            style={{
              whiteSpace: 'normal',
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
            }}
          >
            {title}
          </Text>
        </Box>
      </Box>
      <Box>
        {files.length > 1 && (
          <ActionIcon
            onClick={goNext}
            disabled={!canNext}
            radius={16}
            variant="filled"
            opacity={canNext ? 1 : 0.2}
            bg="rgba(0,0,0,0.22)"
            w={32}
            h={40}
            bd="1px solid rgba(255,255,255,0.10)"
            style={{
              borderRadius: 16,
              backdropFilter: 'blur(10px)',
            }}
          >
            <IconChevronRight size={18} />
          </ActionIcon>
        )}
      </Box>
    </Flex>
  );
}
